import { styled } from "@mui/material/styles";
import Heading from "csi/def/Heading";
import { Renderer } from "csi/heading/Renderer";
import csi from "csi/main";
import { DelayedRequest } from "csi/store/DelayedRequest";
import { StoreNames } from "csi/store/Names";
import { StoreQuery } from "csi/store/Request";
import { Popover } from "designSystem/index";
import { POPOVER_OVERLAP } from "designSystem/styles/popover-positions";
import { ComponentItem } from "designSystem/types/component-item-types";
import { VALUE_TYPE } from "src/c8-platform/type/value";
import React, { FunctionComponent, HTMLAttributes, ReactNode, SyntheticEvent } from "react";

// minimum popup width must be not less then delayed search placeholder min width (plus borders)
export const MIN_STRING_DROPDOWN_WIDTH = 150;
export const DROPDOWN_MAX_WIDTH = 250;

export type InlinePopupGetOptionsFunction = (store: StoreNames | DelayedRequest, query?: StoreQuery, storeOptions?: any) => Promise<GetOptionsProps>;

export interface SelectAction {
	/** value is defined only if select has changed its data */
	onAction: (value: any) => void;

	/** select action button resource */
	res?: string;

	/** select action button label. Work only is res is not specified, otherwise ignored */
	label?: string;
}

export interface GetOptionsProps {
	/** Allow custom options */
	allowCustomOptions?: boolean;

	/** Use delayed search */
	delaySearch?: boolean;

	/** open drop down by default */
	open?: boolean;

	/** select options */
	options: ComponentItem[];

	/** editor component placeholder resource */
	placeholderRes?: string;

	/** select actions */
	selectActions?: SelectAction[];

	store: StoreNames | DelayedRequest;

	/** value */
	value: any;
}

export interface InlinePopupProps {
	/** allow pinyin code or not */
	allowPinyin?: boolean;

	/** type of edit control to use */
	controlType: "MultiSelect" | "SingleSelect";

	/** 
	 * override default select component option renderer 
	 * NOTE: for now only implemented for single select
	*/
	customRenderOption?: (props: HTMLAttributes<HTMLLIElement>, option: ComponentItem, { selected }) => ReactNode;

	/**indicate whether select should be opened */
	dropdownOpen: boolean;

	/** heading to use for rendering */
	heading?: Heading;

	/** callback to receive options and value */
	/** TODO: dunno what type to use here StoreQueryOptions does not have ignoreCase which I seems to supposed to use here */
	getOptions: InlinePopupGetOptionsFunction;

	/** editing cancelled */
	onCancel?: () => void;

	/** callback to update the value */
	onChange: (ev: SyntheticEvent, val: ComponentItem | string) => void;

	/** callback to close the router store popup */
	onDialogClose: VoidFunction;

	/** callback to update last user input */
	onLastInputChange: (value: string) => void;

	/** options to build placeholder */
	placeholderOptions: GetOptionsProps;

	/** ref node to insert popup into */
	refNode: HTMLElement;

	/** renderer used for given inline editing */
	ren: Renderer;

	/** indicate required value */
	required: boolean;
}

/**
 * convert search pattern to regular expression
 */
export function patternToRegExp(pattern: string): RegExp {
	return new RegExp("^" + pattern.replace(/(\\.)|(\*)|(\?)|\W/g, (str, literal, star, question) => {
		return star ? ".*" : question ? "." : literal ? literal : "\\" + str;
	}) + "$", "mi");
}

const StyledPopover = styled(Popover)(({
	theme: Theme,
}) => ({
	["& .MuiPopover-paper"]: {
		padding: 3,
	},
}));

const InlinePopup: FunctionComponent<InlinePopupProps> = (props) => {
	const { controlType, onDialogClose, refNode, ren } = props;
	const { type } = ren;
	const refNodeRect = refNode.getBoundingClientRect();
	const popupTop = refNodeRect.top - 1;
	const popupLeft = refNodeRect.left - 1;
	let popupWidth = refNodeRect.width;
	if (type !== VALUE_TYPE.DOUBLE && type !== VALUE_TYPE.INTEGER) {
		popupWidth = Math.max(popupWidth, MIN_STRING_DROPDOWN_WIDTH);
		if (type !== VALUE_TYPE.STRING) {
			popupWidth = Math.min(popupWidth, DROPDOWN_MAX_WIDTH);
		}
	}

	const InlineControl = csi.syncRequire(controlType)?.default;
	return (
		<StyledPopover
			PaperProps={{ style: { width: popupWidth } }}
			TransitionProps={{ timeout: 0 }}
			anchorPosition={{ top: popupTop, left: popupLeft }}
			anchorReference="anchorPosition"
			disableEnforceFocus
			onClose={onDialogClose}
			open
			{...POPOVER_OVERLAP}
		>
			<InlineControl {...props} />
		</StyledPopover>
	);
};

export default InlinePopup;